import { Logo } from "./Logo";
import { NAV_LINKS, PHONE, PHONE_LINK, WHATSAPP_LINK } from "@/data/content";
import { Linkedin, MapPin } from "lucide-react";

const WhatsAppIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

const Footer = () => (
    <footer className="border-t border-white/10 bg-[#070D1F]" data-testid="footer">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
            <div className="grid md:grid-cols-3 gap-12">
                <div>
                    <Logo />
                    <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-xs">
                        Your Compliance Partner for Labour Law & Statutory Compliance.
                    </p>
                    <p className="mt-3 font-mono2 text-[10px] tracking-[0.2em] uppercase text-sky-400">
                        Registrations • Compliance • Registers • Due Dates
                    </p>
                    <div className="mt-6 flex items-center gap-3">
                        <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" aria-label="WhatsApp" className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-slate-300 hover:text-emerald-400 hover:border-emerald-400/50 transition-colors">
                            <WhatsAppIcon />
                        </a>
                        <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin" aria-label="LinkedIn" className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-slate-300 hover:text-sky-400 hover:border-sky-400/50 transition-colors">
                            <Linkedin size={16} />
                        </a>
                        <a href="https://www.google.com/maps/search/Pramukh+Compliance+Services+Ahmedabad" target="_blank" rel="noopener noreferrer" data-testid="footer-google-business" aria-label="Google Business Profile" className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-slate-300 hover:text-amber-400 hover:border-amber-400/50 transition-colors">
                            <MapPin size={16} />
                        </a>
                    </div>
                </div>
                <div>
                    <p className="eyebrow mb-5">Quick Links</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                        {[
                            { label: "Home", href: "#top" },
                            { label: "About Us", href: "#about" },
                            ...NAV_LINKS,
                            { label: "Gap Assessment", href: "#gap-assessment" },
                        ].map((l) => (
                            <a key={l.label} href={l.href} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm text-slate-400 hover:text-sky-300 transition-colors">
                                {l.label}
                            </a>
                        ))}
                    </div>
                </div>
                <div>
                    <p className="eyebrow mb-5">Contact</p>
                    <p className="text-sm text-slate-300 font-semibold">Hardik Patel</p>
                    <p className="text-sm text-slate-400 mt-1">Pramukh Compliance Services</p>
                    <p className="text-sm text-slate-400">Ahmedabad, Gujarat, India</p>
                    <a href={PHONE_LINK} data-testid="footer-phone" className="mt-3 inline-block font-mono2 text-sm text-sky-300 hover:text-sky-200 transition-colors">
                        {PHONE}
                    </a>
                    <div className="mt-6 flex gap-4 text-xs text-slate-500">
                        <a href="#top" data-testid="footer-privacy" className="hover:text-slate-300 transition-colors">Privacy Policy</a>
                        <a href="#top" data-testid="footer-terms" className="hover:text-slate-300 transition-colors">Terms & Conditions</a>
                    </div>
                </div>
            </div>
            <div className="mt-14 pt-7 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-slate-500">© {new Date().getFullYear()} Pramukh Compliance Services. All rights reserved.</p>
                <p className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 led" />
                    Accepting new compliance engagements
                </p>
            </div>
        </div>
    </footer>
);

export default Footer;
